/**
 * `MicroInteractionFormulaire` — le composant UI **générique** de SPEC-CONTENU §5bis.2
 * (⟦A-4 décision 16⟧) : un QCM administratif, un formulaire absurde, pour casser la routine
 * des choix à boutons de `EvenementModal`. **Ce n'est PAS un mini-jeu d'adresse** : aucun
 * score, aucun chrono, aucune variante d'accessibilité lourde — juste des champs de formulaire
 * qu'on remplit un par un, comme un vrai site administratif.
 *
 * Dialogue **maison** : jamais `window.confirm` / `window.alert` / `window.prompt` (CLAUDE.md).
 * Il ne connaît rien du moteur ni d'`EvenementModal` — pur composant de présentation, piloté
 * uniquement par les props. `decor` est purement décoratif : répondre à ces champs n'a AUCUN
 * effet, ne s'envoie nulle part, ne touche aucun état — c'est juste la manivelle qu'on tourne
 * avant la vraie question. Le DERNIER champ, lui, présente les vrais choix de l'événement
 * (`optionsFinales`, un miroir de `event.choix`) : le sélectionner appelle `onValider(id)`,
 * qui résout l'événement exactement comme un clic sur le bouton normal — la même action
 * `choix_evenement`, les mêmes effets. C'est ça, « sa réponse pilote le choix résolu ».
 */

import { useState } from "react";

export interface ChampDecorMicroInteraction {
  readonly question: string;
  readonly options: readonly string[];
}

export interface OptionFinaleMicroInteraction {
  readonly id: string;
  readonly label: string;
  /** Le `hint` du contenu (SPEC-UI §3.6), tel quel — voir `EvenementModal`. */
  readonly hint?: string;
  /** Phrase d'avertissement déjà composée (strikes, etc.) — le composant ne calcule rien. */
  readonly avertissement?: string;
}

interface Props {
  readonly entete: string;
  readonly decor: readonly ChampDecorMicroInteraction[];
  readonly optionsFinales: readonly OptionFinaleMicroInteraction[];
  readonly onValider: (id: string) => void;
  readonly disabled?: boolean;
}

export function MicroInteractionFormulaire({
  entete,
  decor,
  optionsFinales,
  onValider,
  disabled,
}: Props) {
  const [etape, setEtape] = useState(0);
  const [reponsesDecor, setReponsesDecor] = useState<readonly (string | null)[]>(() =>
    decor.map(() => null),
  );

  const total = decor.length + 1;
  const surDecor = etape < decor.length;
  const champDecor = surDecor ? decor[etape] : undefined;

  return (
    <div className="micro-interaction" role="group" aria-label={entete}>
      <p className="micro-interaction-entete">{entete}</p>
      <p className="micro-interaction-progression">
        Champ {etape + 1} / {total}
      </p>

      {champDecor ? (
        <>
          <p className="micro-interaction-question">{champDecor.question}</p>
          <div className="micro-interaction-options" role="radiogroup" aria-label={champDecor.question}>
            {champDecor.options.map((option) => (
              <label key={option} className="micro-interaction-choix">
                <input
                  type="radio"
                  name={`micro-interaction-champ-${etape}`}
                  checked={reponsesDecor[etape] === option}
                  onChange={() =>
                    setReponsesDecor((r) => r.map((v, i) => (i === etape ? option : v)))
                  }
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
          <div className="dialogue-actions">
            <button
              type="button"
              className="bouton primaire"
              disabled={reponsesDecor[etape] === null}
              onClick={() => setEtape((e) => e + 1)}
            >
              Champ suivant
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="micro-interaction-question">
            Dernier champ — le seul qui compte vraiment.
          </p>
          <div className="micro-interaction-options" role="radiogroup" aria-label="Réponse finale">
            {optionsFinales.map((option) => (
              <label
                key={option.id}
                className="micro-interaction-choix micro-interaction-choix-finale"
              >
                <input
                  type="radio"
                  name="micro-interaction-champ-final"
                  disabled={disabled}
                  onChange={() => onValider(option.id)}
                />
                <span className="micro-interaction-choix-corps">
                  <span className="bouton-option-label">{option.label}</span>
                  {option.hint ? <span className="bouton-option-hint">{option.hint}</span> : null}
                  {option.avertissement ? (
                    <span className="bouton-option-avertissement">{option.avertissement}</span>
                  ) : null}
                </span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
